import React from 'react';
import { Tooltip } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import styled, { useTheme } from 'styled-components';
import { motion } from 'framer-motion';

import { DEFAULT_PERMISSION_DENIED_TOOLTIP } from './Button';

const ENTRY_TRANSITION = { duration: 0.18, ease: 'easeOut' };

/** @param {object} theme */
function isDarkTheme(theme) {
    return theme?.colors?.background?.default === '#141414';
}

function resolveTonePaint(theme, tone) {
    const dark = isDarkTheme(theme);
    if (tone === 'danger') {
        return {
            background: dark ? 'rgba(220, 38, 38, 0.14)' : 'rgba(220, 38, 38, 0.08)',
            color: 'rgb(220, 38, 38)',
            hoverBackground: dark ? 'rgba(220, 38, 38, 0.22)' : 'rgba(220, 38, 38, 0.14)',
        };
    }
    if (tone === 'primary') {
        const p = theme?.colors?.background?.primary || '#7543DC';
        return {
            background: dark ? `${p}33` : `${p}18`,
            color: dark ? '#ffffff' : p,
            hoverBackground: dark ? `${p}55` : `${p}28`,
        };
    }
    return {
        background: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
        color: dark ? 'rgba(255,255,255,0.92)' : 'rgba(0,0,0,0.85)',
        hoverBackground: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
    };
}

/**
 * Multi-segment control: one outer shell, N clickable slices (2–5+), optional color per slice.
 *
 * @param {Array<{
 *   key?: string,
 *   label?: React.ReactNode,
 *   icon?: React.ReactNode,
 *   onClick?: () => void,
 *   disabled?: boolean,
 *   permissionDenied?: boolean,
 *   tooltip?: React.ReactNode,
 *   disabledTooltip?: string,
 *   tone?: 'default' | 'primary' | 'danger',
 *   colors?: { background?: string, color?: string, hoverBackground?: string },
 *   danger?: boolean,
 * }>} segments
 * @param {'single'|'split'} [$chrome] — `split` forces per-slice backgrounds; `single` matches classic unified bar unless slices define `colors`/`tone`.
 */
export function SegmentedButton({
    segments = [],
    permissionDenied = false,
    disabled = false,
    disabledTooltip,
    $size = '14px',
    $padding = '10px 12px',
    $minHeight = '36px',
    $primary = false,
    $blackOnLight = false,
    /** `single`: one filled shell (default). `split`: neutral border, each slice painted. */
    $chrome = 'single',
    $entryAnimation = true,
    $hoverShadow = true,
    /** When true, each segment is sized to its own label/icon (no equal flex stretch) — prevents truncation when labels differ in length. */
    $autoWidth = false,
    isHidden,
    style,
    className,
    ...rest
}) {
    const theme = useTheme();

    if (isHidden || !Array.isArray(segments) || segments.length === 0) return null;

    const hasGlobalPermissionDenied = !!permissionDenied;
    const isDisabled = !!disabled || hasGlobalPermissionDenied;
    const sharedDisabledTooltip = isDisabled && disabledTooltip ? disabledTooltip : undefined;

    const useSplitChrome =
        $chrome === 'split' ||
        segments.some(
            (s) => !!(s?.colors && (s.colors.background || s.colors.color)) || !!(s?.tone && s.tone !== 'default'),
        );

    const inner = (
        <Root
            role="group"
            $size={$size}
            $padding={$padding}
            $primary={$primary}
            $blackOnLight={$blackOnLight}
            $splitChrome={useSplitChrome}
            $hoverShadow={$hoverShadow}
            aria-disabled={isDisabled}
            style={style}
            className={className}
            {...rest}
        >
            {segments.map((seg, index) => {
                const denied = hasGlobalPermissionDenied || !!seg.permissionDenied;
                const segDisabled = isDisabled || denied || !!seg.disabled || !seg.onClick;
                const showLock = denied;
                const tooltipTitle =
                    sharedDisabledTooltip ||
                    (segDisabled && seg.disabledTooltip) ||
                    (denied ? DEFAULT_PERMISSION_DENIED_TOOLTIP : undefined) ||
                    seg.tooltip;

                const tone = seg.tone || 'default';
                const custom = seg.colors || {};
                const tonePaint = useSplitChrome ? resolveTonePaint(theme, tone) : null;
                const background = custom.background ?? (useSplitChrome ? tonePaint?.background : undefined);
                const color = custom.color ?? (useSplitChrome ? tonePaint?.color : undefined);
                const hoverBackground = custom.hoverBackground ?? (useSplitChrome ? tonePaint?.hoverBackground : undefined);
                const hoverColor = custom.hoverColor ?? (useSplitChrome ? tonePaint?.hoverColor : undefined);

                const dangerHover = !useSplitChrome && !!seg.danger;

                const segmentBtn = (
                    <Segment
                        type="button"
                        $size={$size}
                        $padding={$padding}
                        $minHeight={$minHeight}
                        $splitChrome={useSplitChrome}
                        $transparent={!useSplitChrome}
                        $dangerHover={dangerHover}
                        $customBg={background}
                        $customColor={color}
                        $customHoverBg={hoverBackground}
                        $customHoverColor={hoverColor}
                        $autoWidth={$autoWidth}
                        onClick={segDisabled ? undefined : seg.onClick}
                        disabled={segDisabled}
                    >
                        {seg.icon ? <span className="mb-icon">{seg.icon}</span> : null}
                        <span className="mb-label">{seg.label}</span>
                        {showLock ? (
                            <LockOutlined aria-hidden style={{ marginLeft: 4, fontSize: '0.9em', opacity: 0.9 }} />
                        ) : null}
                    </Segment>
                );

                const tooltipTrigger = segDisabled && tooltipTitle ? (
                    <TooltipSegmentWrap>{segmentBtn}</TooltipSegmentWrap>
                ) : (
                    segmentBtn
                );

                return (
                    <React.Fragment key={seg.key ?? index}>
                        {index > 0 ? <Divider aria-hidden $splitChrome={useSplitChrome} /> : null}
                        {tooltipTitle ? (
                            <Tooltip title={tooltipTitle} mouseEnterDelay={0} mouseLeaveDelay={0} placement="top">
                                {tooltipTrigger}
                            </Tooltip>
                        ) : (
                            segmentBtn
                        )}
                    </React.Fragment>
                );
            })}
        </Root>
    );

    const wrap = $entryAnimation ? (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={ENTRY_TRANSITION}
            style={{ display: 'inline-block' }}
        >
            {inner}
        </motion.div>
    ) : (
        <span style={{ display: 'inline-block' }}>{inner}</span>
    );

    return wrap;
}

/**
 * Two-slice group — implemented via {@link SegmentedButton}. Preserves legacy behavior:
 * if either side is permission-denied, the whole control is hidden.
 */
export function MultipleButton({
    leftLabel,
    rightLabel,
    leftIcon,
    rightIcon,
    onLeftClick,
    onRightClick,
    permissionDenied = false,
    leftPermissionDenied = false,
    rightPermissionDenied = false,
    leftTooltip,
    rightTooltip,
    leftDisabledTooltip,
    rightDisabledTooltip,
    disabled,
    disabledTooltip,
    $size = '14px',
    $padding = '10px 12px',
    $minHeight = '36px',
    $primary = false,
    $blackOnLight = false,
    $dangerRight = true,
    $entryAnimation = true,
    $hoverShadow = true,
    /** @type {'single'|'split'} */
    $chrome = 'single',
    /** When true, each segment is sized to its own label (no equal flex stretch) — prevents truncation. */
    $autoWidth = false,
    isHidden,
    style,
    className,
    ...rest
}) {
    const hasGlobalPermissionDenied = !!permissionDenied;
    const leftDenied = hasGlobalPermissionDenied || !!leftPermissionDenied;
    const rightDenied = hasGlobalPermissionDenied || !!rightPermissionDenied;

    if (isHidden) return null;
    if (leftDenied && rightDenied) return null;

    const segments = [];
    if (!leftDenied) {
        segments.push({
            label: leftLabel,
            icon: leftIcon,
            onClick: onLeftClick,
            tooltip: leftTooltip,
            disabledTooltip: leftDisabledTooltip,
        });
    }
    if (!rightDenied) {
        segments.push({
            label: rightLabel,
            icon: rightIcon,
            onClick: onRightClick,
            tooltip: rightTooltip,
            disabledTooltip: rightDisabledTooltip,
            danger: $dangerRight,
        });
    }

    return (
        <SegmentedButton
            segments={segments}
            disabled={disabled}
            disabledTooltip={disabledTooltip}
            $size={$size}
            $padding={$padding}
            $minHeight={$minHeight}
            $primary={$primary}
            $blackOnLight={$blackOnLight}
            $chrome={$chrome}
            $entryAnimation={$entryAnimation}
            $hoverShadow={$hoverShadow}
            $autoWidth={$autoWidth}
            style={style}
            className={className}
            {...rest}
        />
    );
}

const TooltipSegmentWrap = styled.span`
    flex: 1 1 0;
    min-width: 0;
    display: flex;
`;

const Root = styled.div`
    display: inline-flex;
    align-items: stretch;
    border-radius: 8px;
    overflow: hidden;
    padding: 0;
    font-family: var(--font-family-geist);
    border: ${({ theme, $primary, $blackOnLight, $splitChrome }) => {
        if ($splitChrome) {
            const dark = isDarkTheme(theme);
            return dark ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e5e5e5';
        }
        if ($blackOnLight) {
            const dark = theme.colors.background.default === '#141414';
            return dark ? `1px solid ${theme.colors.border.primary || '#7543DC'}` : '1px solid #000000';
        }
        if ($primary) return `1px solid ${theme.colors.border.primary}`;
        const dark = theme.colors.background.default === '#141414';
        return dark ? '1px solid transparent' : '1px solid #E5E5E5';
    }};
    background-color: ${({ theme, $primary, $blackOnLight, $splitChrome }) => {
        if ($splitChrome) {
            return isDarkTheme(theme) ? 'rgba(255,255,255,0.03)' : '#fafafa';
        }
        if ($blackOnLight) {
            const dark = theme.colors.background.default === '#141414';
            return dark ? theme.colors.background.primary || '#7543DC' : '#000000';
        }
        if ($primary) return theme.colors.background.primary;
        const dark = theme.colors.background.default === '#141414';
        return dark ? '#2C2C2C' : '#ffffff';
    }};
    color: ${({ theme, $primary, $blackOnLight, $splitChrome }) => {
        if ($splitChrome) {
            return isDarkTheme(theme) ? 'rgba(255,255,255,0.88)' : 'rgba(0,0,0,0.85)';
        }
        if ($blackOnLight) return '#ffffff';
        if ($primary) return '#ffffff';
        const dark = theme.colors.background.default === '#141414';
        return dark ? '#ffffff' : '#000000';
    }};
    transition: box-shadow 0.25s ease, border-color 0.25s ease, background-color 0.25s ease;
    box-shadow: none;

    &:hover:not([aria-disabled='true']) {
        box-shadow: ${({ $hoverShadow, $splitChrome, theme, $primary }) => {
        if (!$hoverShadow) return 'none';
        if ($splitChrome) {
            return isDarkTheme(theme) ? '0 1px 4px rgba(0,0,0,0.35)' : '0 1px 6px rgba(0,0,0,0.08)';
        }
        if ($primary && theme?.colors?.background?.primary) {
            const hex = theme.colors.background.primary;
            const r = parseInt(hex.slice(1, 3), 16);
            const g = parseInt(hex.slice(3, 5), 16);
            const b = parseInt(hex.slice(5, 7), 16);
            return `0 3px 10px rgba(${r}, ${g}, ${b}, 0.28)`;
        }
        return '0 2px 8px rgba(0, 0, 0, 0.12)';
    }};
    }

    &[aria-disabled='true'] {
        cursor: not-allowed;
        box-shadow: none !important;
        opacity: 1;
        background-color: ${({ theme, $splitChrome }) => {
        if ($splitChrome) {
            return isDarkTheme(theme) ? 'rgba(255,255,255,0.04)' : '#f3f4f6';
        }
        return theme?.colors?.background?.default === '#141414' ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6';
    }};
        color: ${({ theme }) =>
        theme?.colors?.background?.default === '#141414' ? 'rgba(255, 255, 255, 0.35)' : '#9ca3af'};
        border-color: ${({ theme }) =>
        theme?.colors?.background?.default === '#141414' ? 'rgba(255, 255, 255, 0.12)' : '#e5e7eb'};
    }
`;

const Segment = styled.button`
    appearance: none;
    border: none;
    width: ${({ $autoWidth }) => ($autoWidth ? 'auto' : '100%')};
    background: ${({ $transparent, $customBg, $splitChrome }) => {
        if ($customBg) return $customBg;
        if ($splitChrome && !$transparent) return 'transparent';
        return 'transparent';
    }};
    color: ${({ $customColor }) => $customColor || 'inherit'};
    padding: ${({ $padding }) => $padding || '10px 12px'};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: ${({ $size }) => $size || '14px'};
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s ease, color 0.2s ease;
    min-height: ${({ $minHeight }) => $minHeight || '36px'};
    line-height: 1;
    flex: ${({ $autoWidth }) => ($autoWidth ? '0 0 auto' : '1 1 0')};
    min-width: ${({ $autoWidth }) => ($autoWidth ? 'auto' : '0')};
    white-space: nowrap;

    .mb-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
        flex-shrink: 0;
        font-size: 1em;
    }
    .mb-label {
        white-space: nowrap;
        line-height: 1;
        overflow: ${({ $autoWidth }) => ($autoWidth ? 'visible' : 'hidden')};
        text-overflow: ${({ $autoWidth }) => ($autoWidth ? 'clip' : 'ellipsis')};
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 1;
        color: ${({ theme }) => {
        const dark = theme?.colors?.background?.default === '#141414';
        return dark ? 'rgba(255, 255, 255, 0.35)' : '#9ca3af';
    }};
        background: ${({ theme, $customBg }) => {
        if ($customBg) return $customBg;
        const dark = theme?.colors?.background?.default === '#141414';
        return dark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6';
    }};
    }

    &:hover:not(:disabled) {
        background: ${({ theme, $dangerHover, $customHoverBg, $splitChrome, $customBg }) => {
        if ($customHoverBg) return $customHoverBg;
        if ($dangerHover) {
            const dark = theme?.colors?.background?.default === '#141414';
            return dark ? 'rgba(220, 38, 38, 0.16)' : 'rgba(220, 38, 38, 0.10)';
        }
        if ($splitChrome && $customBg) {
            const dark = theme?.colors?.background?.default === '#141414';
            return dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)';
        }
        const dark = theme?.colors?.background?.default === '#141414';
        return dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)';
    }};
        color: ${({ $customHoverColor, $dangerHover }) => {
            if ($customHoverColor) return $customHoverColor;
            return $dangerHover ? 'rgb(220, 38, 38)' : 'inherit';
        }};
    }
`;

const Divider = styled.span`
    width: 1px;
    align-self: stretch;
    flex-shrink: 0;
    background: ${({ theme, $splitChrome }) => {
        if ($splitChrome) {
            return isDarkTheme(theme) ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';
        }
        return 'rgba(0, 0, 0, 0.08)';
    }};
    ${({ theme, $splitChrome }) =>
        !$splitChrome && isDarkTheme(theme)
            ? `
        background: rgba(255,255,255,0.12);
      `
            : ''}
`;

export default MultipleButton;
